import { weekFileName } from '../../lib/weekFileName';
import { DocLangToggle } from '../ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import { WeekFilter } from '../ui/week-filter';
import { matchesWeek } from '../../lib/weekFilter';
import i18n from '../../i18n';
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Plus, Search, GraduationCap, Filter, Trash2, 
  Clock, Users, Award, Calendar, Link2, Eye, FileDown, BarChart3, FileText
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import EntityLinker, { EntityLinkBadge } from './EntityLinker';
import { TrainingDetailDialog } from './ISODetailDialogs';
import { DateText } from '../ui/week-badge';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const TRAINING_TYPES = [
  { value: 'internal', label: i18n.t('auto.lbl.internalTraining') },
  { value: 'external', label: i18n.t('auto.lbl.externalTraining') },
  { value: 'online', label: i18n.t('auto.lbl.onlineELearning') },
  { value: 'on_the_job', label: i18n.t('auto.lbl.onTheJobTraining') },
];

const TRAINING_STATUS = [
  { value: 'planned', label: i18n.t('auto.lbl.planned'), color: 'bg-blue-100 text-blue-800' },
  { value: 'in_progress', label: i18n.t('auto.lbl.inProgress'), color: 'bg-yellow-100 text-yellow-800' },
  { value: 'completed', label: i18n.t('auto.lbl.completed'), color: 'bg-green-100 text-green-800' },
  { value: 'expired', label: i18n.t('auto.lbl.expired'), color: 'bg-red-100 text-red-800' },
  { value: 'cancelled', label: i18n.t('auto.lbl.cancelled'), color: 'bg-gray-100 text-gray-800' },
];

const TrainingRecords = ({ training, employees, projects, documents, onRefresh, token }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [weekFilter, setWeekFilter] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Training templates and auto-numbering
  const [trainingTemplates, setTrainingTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  const [formData, setFormData] = useState({
    training_number: '',
    title: '',
    description: '',
    url: '',
    training_type: 'internal',
    provider: '',
    scheduled_date: format(new Date(), 'yyyy-MM-dd'),
    duration_hours: 1,
    employee_ids: [],
    trainer_id: '',
    certification_required: false,
    certification_name: '',
    certification_expiry: '',
    pass_criteria: '',
    passing_score: '',
    google_sheets_url: '',
    linked_document_ids: [],
    linked_project_ids: [],
  });

  const [selectedEmployees, setSelectedEmployees] = useState([]);

  // Fetch training templates
  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/settings/training-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTrainingTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching training templates:', error);
    }
  }, [token]);

  // Fetch next auto-number
  const fetchNextNumber = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/settings/next-number/training`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        return data.next_number;
      }
    } catch (error) {
      console.error('Error fetching next number:', error);
    }
    return '';
  }, [token]);

  // Initialize templates on mount
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Handle opening create dialog with auto-number
  const handleOpenCreateDialog = async () => {
    const nextNumber = await fetchNextNumber();
    setFormData(prev => ({
      ...prev,
      training_number: nextNumber || prev.training_number,
      title: '',
      description: '',
      url: '',
      training_type: 'internal',
      provider: '',
      scheduled_date: format(new Date(), 'yyyy-MM-dd'),
      duration_hours: 1,
      certification_required: false,
      certification_name: '',
      certification_expiry: '',
      pass_criteria: '',
      passing_score: '',
      google_sheets_url: '',
      linked_document_ids: [],
      linked_project_ids: [],
    }));
    setSelectedEmployees([]);
    setSelectedTemplate('');
    setShowCreateDialog(true);
  };

  // Handle template selection
  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    
    if (!templateId || templateId === 'none') return;
    
    const template = trainingTemplates.find(t => t.id === templateId);
    if (template) {
      setFormData(prev => ({
        ...prev,
        title: template.title || prev.title,
        description: template.description || '',
        training_type: template.training_type || 'external',
        provider: template.provider || '',
        duration_hours: template.duration_hours || 8,
        certification_required: template.certification_required || false,
        certification_name: template.certification_name || '',
        pass_criteria: template.pass_criteria || '',
        passing_score: template.passing_score || '',
        // Calculate certification expiry if validity months is set
        certification_expiry: template.certification_validity_months && template.certification_required 
          ? format(new Date(new Date().setMonth(new Date().getMonth() + template.certification_validity_months)), 'yyyy-MM-dd')
          : prev.certification_expiry
      }));
      toast.success(`Template "${template.name}" applied`);
    }
  };

  const filteredTraining = training.filter(t => {
    const matchesSearch = t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          t.training_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || t.status === filterStatus;
    const matchesType = filterType === 'all' || t.training_type === filterType;
    return matchesSearch && matchesStatus && matchesType && matchesWeek(weekFilter, t.scheduled_date || t.created_at);
  });

  const handleCreate = async () => {
    if (!formData.training_number || !formData.title) {
      toast.error(t('auto.iso.trainingNumberAndTitleAreRequired'));
      return;
    }

    setIsLoading(true);
    try {
      const body = {
        ...formData,
        employee_ids: selectedEmployees,
      };
      if (formData.scheduled_date) {
        body.scheduled_date = new Date(formData.scheduled_date).toISOString();
      }
      if (formData.certification_expiry) {
        body.certification_expiry = new Date(formData.certification_expiry).toISOString();
      }

      const response = await fetch(`${API_BASE}/api/iso/training`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create training');
      }

      // Increment the auto-number for next record
      try {
        await fetch(`${API_BASE}/api/settings/increment-number/training`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (err) {
        console.error('Error incrementing number:', err);
      }

      toast.success(t('auto.iso.trainingRecordCreatedSuccessfully'));
      setShowCreateDialog(false);
      setSelectedTemplate('');
      setFormData({
        training_number: '',
        title: '',
        description: '',
        url: '',
        training_type: 'internal',
        provider: '',
        scheduled_date: format(new Date(), 'yyyy-MM-dd'),
        duration_hours: 1,
        employee_ids: [],
        trainer_id: '',
        certification_required: false,
        certification_name: '',
        certification_expiry: '',
        pass_criteria: '',
        passing_score: '',
        google_sheets_url: '',
        linked_document_ids: [],
        linked_project_ids: [],
      });
      setSelectedEmployees([]);
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (trainingId, newStatus) => {
    try {
      const body = { status: newStatus };
      if (newStatus === 'completed') {
        body.completed_date = new Date().toISOString();
      }

      const response = await fetch(`${API_BASE}/api/iso/training/${trainingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error('Failed to update status');
      toast.success(t('auto.iso.trainingStatusUpdated'));
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (trainingId) => {
    if (!window.confirm(t('auto.iso.areYouSureYouWantTo3'))) return;

    try {
      const response = await fetch(`${API_BASE}/api/iso/training/${trainingId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to delete training');
      toast.success(t('auto.iso.trainingRecordDeleted'));
      onRefresh();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleExportCompetencyReport = async () => {
    try {
      toast.info(t('auto.iso.generatingCompetencyReport'));
      const response = await fetch(`${API_BASE}/api/iso/training/competency-report/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to generate report');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = weekFileName(`Training_Competency_Report_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(t('auto.iso.competencyReportDownloaded'));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getEmployeeName = (id) => {
    const emp = employees.find(e => e.id === id);
    return emp?.name || 'Unknown';
  };

  const getStatusBadge = (status) => {
    const statusInfo = TRAINING_STATUS.find(s => s.value === status);
    return <Badge className={statusInfo?.color}>{statusInfo?.label || status}</Badge>;
  };

  const getTypeBadge = (type) => {
    const typeInfo = TRAINING_TYPES.find(t => t.value === type);
    return <Badge variant="outline">{typeInfo?.label || type}</Badge>;
  };

  const toggleEmployee = (empId) => {
    setSelectedEmployees(prev => 
      prev.includes(empId) 
        ? prev.filter(id => id !== empId)
        : [...prev, empId]
    );
  };

  return (
    <div className="space-y-4" data-testid="training-records">
      {/* Header */}
      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <div className="flex flex-1 flex-wrap gap-2">
          <div className="relative min-w-[180px] flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t('auto.iso.searchTraining')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <WeekFilter value={weekFilter} onChange={setWeekFilter} testId="training-week-filter" />
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[150px] max-w-full">
              <SelectValue placeholder={t('auto.iso.type')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('auto.iso.allTypes')}</SelectItem>
              {TRAINING_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px] max-w-full">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder={t('auto.iso.status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('auto.iso.allStatus')}</SelectItem>
              {TRAINING_STATUS.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <DocLangToggle testId="doc-lang-trainingrecords-0" />
          <Button 
            variant="outline" 
            onClick={handleExportCompetencyReport}
            data-testid="export-competency-btn"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            {t('auto.iso.competencyReport')}
          </Button>
          <Button onClick={handleOpenCreateDialog} data-testid="create-training-btn">
            <Plus className="h-4 w-4 mr-2" />
            {t('auto.iso.newTraining')}
          </Button>
        </div>
      </div>

      {/* Training List */}
      <div className="grid gap-4">
        {filteredTraining.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              <GraduationCap className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>{t('auto.iso.noTrainingRecordsFound')}</p>
            </CardContent>
          </Card>
        ) : (
          filteredTraining.map(tItem => (
            <Card 
              key={tItem.id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => {
                setSelectedTraining(tItem);
                setShowDetailDialog(true);
              }}
              data-testid={`training-card-${tItem.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <GraduationCap className="h-4 w-4 text-indigo-600" />
                      <span className="font-mono text-sm text-gray-500">{tItem.training_number}</span>
                      {getTypeBadge(tItem.training_type)}
                      {getStatusBadge(tItem.status)}
                      {tItem.certification_required && (
                        <Badge className="bg-purple-100 text-purple-800">
                          <Award className="h-3 w-3 mr-1" />
                          {t('auto.iso.certification')}
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-medium">{tItem.title}</h3>
                    {tItem.description && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{tItem.description}</p>
                    )}
                    {(() => {
                      const total = tItem.employee_ids?.length || 0;
                      if (total === 0) return null;
                      const results = tItem.participant_results || {};
                      let passed = 0, failed = 0, attended = 0, enrolled = 0, excused = 0, certsIssued = 0;
                      (tItem.employee_ids || []).forEach(empId => {
                        const r = results[empId] || {};
                        const s = r.status || 'enrolled';
                        if (s === 'passed') passed++;
                        else if (s === 'failed') failed++;
                        else if (s === 'attended') attended++;
                        else if (s === 'excused') excused++;
                        else enrolled++;
                        if (r.certificate_issued) certsIssued++;
                      });
                      const certsPending = tItem.certification_required ? Math.max(0, passed - certsIssued) : 0;
                      const needsGrading = (tItem.status === 'completed' || tItem.status === 'in_progress') &&
                        (enrolled > 0 || attended > 0);
                      const actionable = needsGrading || certsPending > 0;
                      return (
                        <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`training-progress-${tItem.id}`}>
                          {passed > 0 && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-[11px] font-normal">
                              {passed} passed
                            </Badge>
                          )}
                          {failed > 0 && (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[11px] font-normal">
                              {failed} failed
                            </Badge>
                          )}
                          {attended > 0 && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[11px] font-normal">
                              {attended} attended
                            </Badge>
                          )}
                          {enrolled > 0 && (
                            <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200 text-[11px] font-normal">
                              {enrolled} enrolled
                            </Badge>
                          )}
                          {excused > 0 && (
                            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-[11px] font-normal">
                              {excused} excused
                            </Badge>
                          )}
                          {tItem.certification_required && (
                            <Badge
                              variant="outline"
                              className={`text-[11px] font-normal ${
                                certsPending > 0
                                  ? 'bg-amber-50 text-amber-700 border-amber-300'
                                  : certsIssued > 0
                                    ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                    : 'bg-gray-50 text-gray-500 border-gray-200'
                              }`}
                            >
                              <Award className="h-3 w-3 mr-1" />
                              {certsIssued}/{passed || total} certs
                              {certsPending > 0 && ` · ${certsPending} pending`}
                            </Badge>
                          )}
                          {actionable && (
                            <span className="text-[11px] text-amber-600 font-medium ml-1" title={t('auto.iso.actionRequired')}>
                              ● action required
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400">
                      {tItem.scheduled_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {<DateText value={tItem.scheduled_date} />}
                        </span>
                      )}
                      {tItem.duration_hours && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {tItem.duration_hours} hours
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {tItem.employee_ids?.length || 0} participants
                      </span>
                      {tItem.provider && (
                        <span>Provider: {tItem.provider}</span>
                      )}
                      {tItem.certification_expiry && (
                        <span className={`flex items-center gap-1 ${new Date(tItem.certification_expiry) < new Date() ? 'text-red-500' : ''}`}>
                          <Award className="h-3 w-3" />
                          Expires: {<DateText value={tItem.certification_expiry} />}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedTraining(tItem);
                        setShowDetailDialog(true);
                      }}
                      data-testid={`view-training-${tItem.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Select
                      value={tItem.status}
                      onValueChange={(value) => handleUpdateStatus(tItem.id, value)}
                    >
                      <SelectTrigger className="h-8 w-[130px] max-w-full text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TRAINING_STATUS.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600"
                      onClick={() => handleDelete(tItem.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Training Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('auto.iso.createTrainingRecord')}</DialogTitle>
            <DialogDescription className="sr-only">{t('auto.iso.createTrainingRecord')}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Template Selector */}
            {trainingTemplates.length > 0 && (
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <label className="text-sm font-medium text-purple-800 flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4" />
                  {t('auto.iso.useTrainingTemplateOptional')}
                </label>
                <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                  <SelectTrigger data-testid="training-template-selector">
                    <SelectValue placeholder={t('auto.iso.selectATemplateToAutoFill')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('auto.iso.noneManualEntry')}</SelectItem>
                    {trainingTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} - {t.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-purple-600 mt-1">
                  {t('auto.iso.selectAPredefinedTemplateToAuto')}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('auto.iso.trainingNumber')}</label>
                <Input
                  value={formData.training_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, training_number: e.target.value  }))}
                  placeholder={t('auto.iso.autoGenerated')}
                  data-testid="training-number-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.type3')}</label>
                <Select
                  value={formData.training_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, training_type: value  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRAINING_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.title')}</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value  }))}
                placeholder={t('auto.iso.trainingTitle')}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.description')}</label>
              <textarea
                className="w-full min-h-[60px] p-2 border rounded-md text-sm"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value  }))}
                placeholder={t('auto.iso.trainingDescription')}
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('auto.iso.trainingUrlOptional')}</label>
              <Input
                value={formData.url}
                onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value  }))}
                placeholder="https://training-portal.com/course"
                data-testid="training-url-input"
              />
              <p className="text-xs text-gray-500 mt-1">{t('auto.iso.linkToExternalTrainingMaterialsOr')}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('auto.iso.scheduledDate')}</label>
                <Input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, scheduled_date: e.target.value  }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.durationHours')}</label>
                <Input
                  type="number"
                  value={formData.duration_hours}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration_hours: parseFloat(e.target.value) || 1  }))}
                  min="0.5"
                  step="0.5"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('auto.iso.provider')}</label>
                <Input
                  value={formData.provider}
                  onChange={(e) => setFormData(prev => ({ ...prev, provider: e.target.value  }))}
                  placeholder={t('auto.iso.trainingProvider')}
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.trainer')}</label>
                <Select
                  value={formData.trainer_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, trainer_id: value  }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('auto.iso.selectTrainer')} />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Assessment/Scoring */}
            <div className="p-3 bg-gray-50 rounded-lg space-y-3">
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                {t('auto.iso.assessmentScoring')}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('auto.iso.passingScore')}</label>
                  <Input
                    type="number"
                    value={formData.passing_score}
                    onChange={(e) => setFormData(prev => ({ ...prev, passing_score: e.target.value  }))}
                    placeholder="e.g., 80"
                    min="0"
                    max="100"
                    data-testid="training-passing-score-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('auto.iso.minimumToPass')}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">{t('auto.iso.passCriteria')}</label>
                  <Input
                    value={formData.pass_criteria}
                    onChange={(e) => setFormData(prev => ({ ...prev, pass_criteria: e.target.value  }))}
                    placeholder="e.g., Min 80% score"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">{t('auto.iso.googleSheetsUrlForAutoScoring')}</label>
                <Input
                  value={formData.google_sheets_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, google_sheets_url: e.target.value  }))}
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  data-testid="training-google-sheets-url-input"
                />
                <p className="text-xs text-gray-500 mt-1">{t('auto.iso.linkToGoogleSheetsContainingForm')}</p>
              </div>
            </div>
            
            {/* Participants */}
            <div>
              <label className="text-sm font-medium">{t('auto.iso.participants')}</label>
              <div className="border rounded-md p-2 max-h-[120px] overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {employees.map(emp => (
                    <label key={emp.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={selectedEmployees.includes(emp.id)}
                        onChange={() => toggleEmployee(emp.id)}
                        className="rounded"
                      />
                      {emp.name}
                    </label>
                  ))}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">{selectedEmployees.length} selected</p>
            </div>

            {/* Certification */}
            <div className="border-t pt-4">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.certification_required}
                  onChange={(e) => setFormData(prev => ({ ...prev, certification_required: e.target.checked  }))}
                  className="rounded"
                />
                {t('auto.iso.certificationRequired2')}
              </label>
              {formData.certification_required && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                  <div>
                    <label className="text-sm font-medium">{t('auto.iso.certificationName')}</label>
                    <Input
                      value={formData.certification_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, certification_name: e.target.value  }))}
                      placeholder={t('auto.iso.certificationName2')}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">{t('auto.iso.expiryDate')}</label>
                    <Input
                      type="date"
                      value={formData.certification_expiry}
                      onChange={(e) => setFormData(prev => ({ ...prev, certification_expiry: e.target.value  }))}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} data-testid="dialog-cancel-btn">{t('auto.iso.cancel')}</Button>
            <Button onClick={handleCreate} disabled={isLoading} data-testid="dialog-create-btn">
              {isLoading ? t('auto.iso.creating') : t('auto.iso.createTraining')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Training Detail Dialog */}
      <TrainingDetailDialog
        open={showDetailDialog}
        onOpenChange={setShowDetailDialog}
        training={selectedTraining}
        employees={employees}
        projects={projects}
        token={token}
        onRefresh={onRefresh}
      />
    </div>
  );
};

export default TrainingRecords;
